<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';
    import { OTP_CONSECUTIVE_DELAY } from '$lib/global/constants';

    import { page } from '$app/stores';
    import OtpVerificationModal from '$lib/components/Modals/OtpVerificationModal';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import Uploader from '$lib/global/helper/Uploader';
    import { otpApi } from '$lib/global/otp/api';
    import {
        trackCalendly,
        trackCal,
        trackGHL,
        trackHubSpot,
        trackFormSubmit,
    } from '$lib/global/tracking';
    import { getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import { advancedLinking } from '$lib/Stores/stores';
    import { OtpVerificationError } from '$lib/types/Error';
    import { EmailValidationKind, PhoneValidationKind } from '$lib/types/PhoneValidationKind';
    import { getLinkingPreloadTarget, handleLinkingNavigation } from '$lib/utils/linkingNavigation';
    import { storeUserAnswers, getAllUserAnswers } from '$lib/utils/userAnswerStorage';

    import get from 'lodash/get';
    import { onMount } from 'svelte';
    import { openModal } from 'svelte-promise-modals';

    import CalendlyForm from './CalendlyForm.svelte';
    import CalForm from './CalForm.svelte';
    import { CalendarProvider } from './constants';
    import FormQuestion from './FormQuestion.View.svelte';
    import GHLForm from './GHLForm.svelte';
    import HubSpotForm from './HubSpotForm.svelte';
    import { cal } from '../_utils/cal';
    import { ghl } from '../_utils/ghl';
    import { sanitizeValues } from '../_utils/helpers';
    import { hubspot } from '../_utils/hubspot';

    import type { FormQuestionContent } from '$lib/types/Components/FormQuestion/FormQuestionContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { Field } from '$lib/types/Field';
    import type { FormValues } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { ComponentType } from 'svelte';

    const getCalendarFormComponent = (
        calendarUrl?: string,
        provider?: CalendarProvider,
    ): ComponentType => {
        if (
            provider === CalendarProvider.HUBSPOT ||
            (calendarUrl && hubspot.validateUrl(calendarUrl))
        ) {
            return HubSpotForm as ComponentType;
        }

        if (provider === CalendarProvider.GHL || (calendarUrl && ghl.validateUrl(calendarUrl))) {
            return GHLForm as ComponentType;
        }

        if (provider === CalendarProvider.CAL || (calendarUrl && cal.validateUrl(calendarUrl))) {
            return CalForm as ComponentType;
        }

        return CalendlyForm as ComponentType;
    };

    const trackCalendarBooking = (calendarUrl?: string, provider?: CalendarProvider): void => {
        if (
            provider === CalendarProvider.HUBSPOT ||
            (calendarUrl && hubspot.validateUrl(calendarUrl))
        ) {
            trackHubSpot();
            return;
        }

        if (provider === CalendarProvider.GHL || (calendarUrl && ghl.validateUrl(calendarUrl))) {
            trackGHL();
            return;
        }

        if (provider === CalendarProvider.CAL || (calendarUrl && cal.validateUrl(calendarUrl))) {
            trackCal();
            return;
        }

        trackCalendly();
    };

    export let id = '';
    export let content: FormQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let companyId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { inputMap, pageIdSlugMap },
    } = $page;

    const form = childComponents[0];
    const formType = form.componentType;
    const inputs: InputData[] = [];

    // File Upload
    let fileToUpload: File;
    let fileUploading = false;
    let uploadedFileUrl = '';
    let fileInputFieldName = '';
    const uploader = new Uploader(companyId, campaignId);

    // Button
    const button = form.components.find(
        (component: Component) => component.componentType === 'button',
    );
    const btnBgColor = getColor(
        get(button, 'content.backgroundColor'),
        theme.buttonBackgroundColor,
    );
    const btnColor = getColor(get(button, 'content.color'), getColorByLightness(btnBgColor));

    // Navigation
    const externalLink = get(button, 'content.linking.url') as string;
    const isNewTab = get(button, 'content.linking.isNewTab', false);
    const nextPageNavigator = new NextPageNavigator(
        campaignId,
        firstResultPage,
        pageIdSlugMap,
        trackingProperties,
    );
    const isExternal = !get(button, 'content.linking.isInternal', true) && !!externalLink;
    const originalLogicNextPage = get(button, 'content.linking.pageId', nextPage) as string;
    const linkingComponentId = get(button, 'content.linking.componentId');

    let FormComponent: ComponentType;

    const personalizedText = personalizeText(
        content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    const personalizedButtonText = personalizeText(
        button?.content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    onMount(async () => {
        if (formType === 'formURL') {
            const calendarUrl = form.content.url;
            const provider = form.content.provider;
            FormComponent = getCalendarFormComponent(calendarUrl, provider);
        } else {
            FormComponent = FormQuestion;
        }
    });

    const handleFileAccept = (file: File) => {
        fileToUpload = file;
    };

    const phoneFieldComponent = form.components.find(
        (component) => component.content.fieldName === 'phone',
    );

    const emailFieldComponent = form.components.find(
        (component) => component.content.fieldName === 'email',
    );

    const isNewPhoneField =
        phoneFieldComponent?.content.customOptions?.phoneValidationKind ===
        PhoneValidationKind.CountryFormat;

    const isPhoneVerificationCodeField =
        phoneFieldComponent?.content.customOptions?.phoneValidationKind ===
        PhoneValidationKind.VerificationCode;

    const isEmailVerificationCodeField =
        emailFieldComponent?.content.customOptions?.emailValidationKind ===
        EmailValidationKind.VerificationCode;

    const otpCapabilities = {
        phone: false,
        email: false,
    };

    form.components.forEach((component: Component) => {
        if (component.componentType === 'input') {
            const inputData: InputData = {
                type: component.content.inputType,
                name: get(
                    inputMap,
                    `byComponent[${component._id}].name`,
                    component.content.fieldName,
                ),
                emoji: component.content.icon,
                dateFormat: component.content.dateFormat,
                customOptions: component.content.customOptions,
                showEmoji: component.content.showIcon,
                required: component.content.required,
                conversionTrigger: component.content.conversionTrigger,
                placeholder: component.content.placeholder,
                wysiwyg: get(component, 'content.misc.wysiwyg', {}),
                autocomplete: get(inputMap, `byComponent[${component._id}].autocomplete`, ''),
                onFileAccept: undefined as unknown as (file: File) => void,
            };

            if (component.content.inputType === 'file') {
                inputData.onFileAccept = handleFileAccept;
                fileInputFieldName = inputData.name;
            }

            inputs.push(inputData);
        }
    });

    const uploadFile = async () => {
        fileUploading = true;

        try {
            uploadedFileUrl = await uploader.uploadS3(fileToUpload);
        } catch (err) {
            uploadedFileUrl = 'failed-upload';
            console.warn('File upload failed', { err });
        } finally {
            fileUploading = false;
        }
    };

    const trackSubmit = (values: FormValues) => {
        trackFormSubmit({ form, button, values, fields, inputMap });
    };

    const uploadFileIfNecessary = async (values: FormValues) => {
        if (values[fileInputFieldName]) {
            await uploadFile();

            values[fileInputFieldName] = uploadedFileUrl;
        }
    };

    const checkOtpCapabilities = async () => {
        try {
            if (isPhoneVerificationCodeField || isEmailVerificationCodeField) {
                const capabilities = await otpApi.getOtpCapabilities(companyId);

                otpCapabilities.phone = capabilities.data.phone;
                otpCapabilities.email = capabilities.data.email;
            }
        } catch (error) {
            console.warn('Verifying OTP capabilities failed', { error });
        }
    };

    const verifyPhoneNumber = async (values: FormValues) => {
        if (isPhoneVerificationCodeField && otpCapabilities.phone && values['phone']) {
            const verificationResult = await openModal(OtpVerificationModal, {
                value: values['phone'] as string,
                type: 'phone',
                theme,
                language,
                campaignId,
                campaignVersionId,
            });

            if (!verificationResult) {
                throw new OtpVerificationError('Phone number verification failed');
            }
        }

        return Promise.resolve(true);
    };

    const verifyEmail = async (values: FormValues) => {
        if (isEmailVerificationCodeField && otpCapabilities.email && values['email']) {
            const verificationResult = await openModal(OtpVerificationModal, {
                value: values['email'] as string,
                type: 'email',
                theme,
                language,
                campaignId,
                campaignVersionId,
            });

            if (!verificationResult) {
                throw new OtpVerificationError('Email verification failed');
            }
        }

        return Promise.resolve(true);
    };

    const otpVerification = async (values: FormValues) => {
        await checkOtpCapabilities();

        if (!otpCapabilities.phone && !otpCapabilities.email) {
            return;
        }

        await verifyEmail(values);

        // Required for consecutive OTP verifications for modal hide and show
        if (isPhoneVerificationCodeField && isEmailVerificationCodeField && otpCapabilities.email) {
            await new Promise((resolve) => setTimeout(resolve, OTP_CONSECUTIVE_DELAY));
        }

        await verifyPhoneNumber(values);
    };

    const handleCalendarSubmit = async (values: FormValues) => {
        await uploadFileIfNecessary(values);
        const sanitizedValues = sanitizeValues(
            values,
            isNewPhoneField,
            isPhoneVerificationCodeField,
        );

        trackSubmit(sanitizedValues);
    };

    // Fired after Calendar Submit
    const handleSubmit = async (values: FormValues) => {
        try {
            await uploadFileIfNecessary(values);
            const sanitizedValues = sanitizeValues(
                values,
                isNewPhoneField,
                isPhoneVerificationCodeField,
            );

            if (formType === 'formURL') {
                // Track Calendar booking event; formData has already been tracked (handleCalendarSubmit)
                // OTP verification is handled in the CalendarForm/CalForm component

                const calendarUrl = form.content.url;
                const provider = form.content.provider;
                trackCalendarBooking(calendarUrl, provider);
            } else {
                trackSubmit(sanitizedValues);
                await otpVerification(sanitizedValues);
            }

            // Map sanitized values to Form Input Ids
            const mappedValues: Record<string, string> = {};
            Object.keys(sanitizedValues).forEach((key) => {
                const inputId = get(inputMap, `byName[${form._id}][${key}]`);

                if (inputId) {
                    mappedValues[inputId] = sanitizedValues[key] as string;
                }
            });

            // Store updated answers (including current page data)
            storeUserAnswers({ campaignVersionId, answers: mappedValues });

            // Get all answers for advanced linking evaluation
            const allAnswers = { ...getAllUserAnswers(campaignVersionId), ...mappedValues };

            return handleLinkingNavigation({
                componentId: button?._id || '',
                userAnswers: allAnswers, // Now includes previous + current answers
                advancedLinking: $advancedLinking,
                nextPageNavigator,
                originalLogicNextPage,
                nextPage,
                externalLink,
                isExternal,
                isNewTab,
                currentPageId: $page.data.pageId,
                linkingComponentId,
            });
        } catch (error) {
            if (error instanceof OtpVerificationError) {
                return;
            }

            throw error;
        }
    };

    const handleMouseEnter = () => {
        const { pageId, isExternal: isExternalTarget } = getLinkingPreloadTarget({
            componentId: button?._id || '',
            userAnswers: getAllUserAnswers(campaignVersionId),
            advancedLinking: $advancedLinking,
            nextPageNavigator,
            originalLogicNextPage,
            nextPage,
            isExternal,
        });

        nextPageNavigator.preload(pageId, isExternalTarget);
    };
</script>

<svelte:component
    this={FormComponent}
    {id}
    draft={personalizedText}
    color={getColor(content.color, theme.fontColor)}
    align={content.align}
    onSubmit={handleSubmit}
    onCalendarFormSubmit={handleCalendarSubmit}
    onMouseEnter={handleMouseEnter}
    box={content.box}
    btnId={button?._id}
    btnDraft={personalizedButtonText}
    btnSize={button?.content.size}
    btnAlign={button?.content.align}
    btnFullWidthOnDesktop={button?.content.fullWidthOnDesktop}
    btnAnimation={get(button, 'content.animation', '')}
    btnStyle={get(button, 'content.buttonStyle', 'filled')}
    calendarUrl={form.content.url}
    disabled={fileUploading}
    otpVerificationParams={formType === 'formURL'
        ? {
              otpVerification: otpVerification,
              isNewPhoneField,
              isPhoneVerificationCodeField,
          }
        : null}
    {campaignId}
    {fileUploading}
    {btnBgColor}
    {btnColor}
    {inputs}
    {transitionDelay}
    {transitionEffect}
    {layout}
    {language}
    {theme}
    provider={form.content.provider}
/>
